import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowLeft, Users, ShieldCheck } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * Preview-only route — renders BOTH proposed layouts for the Features section
 * of the Holborough Lakes Social case study, so the user can pick.
 * Route: /features-preview/holborough
 */
export default function FeaturesPreview() {
  const [demo, setDemo] = useState(null);

  useEffect(() => {
    api.get("/demos/holborough-social").then(({ data }) => setDemo(data));
  }, []);

  if (!demo) return <div className="app-container py-32 text-center font-mono text-zinc-500">Loading…</div>;

  const all = demo.features || [];
  const residents = all
    .filter((f) => /^Residents\s+[—–-]\s+/.test(f))
    .map((f) => f.replace(/^Residents\s+[—–-]\s+/, ""));
  const committee = all
    .filter((f) => /^(HLSC|Committee)\s+[—–-]\s+/.test(f))
    .map((f) => f.replace(/^(HLSC|Committee)\s+[—–-]\s+/, ""));
  const usesHLSC = all.some((f) => /^HLSC\s+[—–-]\s+/.test(f));
  const adminLabel = usesHLSC ? "For HLSC" : "For the committee";

  return (
    <div className="bg-zinc-50 min-h-screen">
      <div className="app-container pt-10">
        <Link to="/demos/holborough-social" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900">
          <ArrowLeft size={14} /> Back to Holborough case study
        </Link>
      </div>

      <div className="app-container py-10">
        <p className="overline mb-3 text-[hsl(var(--appistry-signal))]">Layout preview · Features section</p>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Pick a layout.
        </h1>
        <p className="text-lg text-zinc-600 max-w-2xl mb-2">
          Same {all.length} features, two different visual treatments. The current live version mixes Residents and Committee bullets into one alternating grid — hard to scan.
        </p>
        <p className="text-sm font-mono text-zinc-500">
          {residents.length} residents-facing · {committee.length} {usesHLSC ? "HLSC-only" : "committee-only"}
        </p>
      </div>

      {/* OPTION A — Stacked subsections */}
      <section className="border-y border-zinc-200 bg-white">
        <div className="app-container py-14 md:py-16">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-10 w-10 bg-[hsl(var(--appistry-signal))] text-white flex items-center justify-center font-black text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>A</div>
            <div>
              <p className="overline text-zinc-500">Option A</p>
              <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Stacked — two sub-sections, full width each</h2>
            </div>
          </div>

          {/* Residents sub-section */}
          <div className="mb-10">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5 pb-3 border-b-2 border-zinc-950">
              <div className="flex items-center gap-3">
                <Users size={24} weight="duotone" className="text-[hsl(var(--appistry-signal))]" />
                <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>For residents</h3>
              </div>
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">{residents.length} features · public-facing</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-zinc-200">
              {residents.map((f, i) => (
                <div key={i} className="p-5 border-r border-b border-zinc-200 flex gap-3 items-start bg-white">
                  <CheckCircle size={20} weight="fill" className="text-[hsl(var(--appistry-signal))] flex-shrink-0 mt-0.5" />
                  <p className="text-zinc-800 leading-relaxed text-sm md:text-base">{f}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Committee sub-section */}
          <div>
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5 pb-3 border-b-2 border-zinc-950">
              <div className="flex items-center gap-3">
                <ShieldCheck size={24} weight="duotone" className="text-zinc-900" />
                <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{adminLabel}</h3>
              </div>
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">{committee.length} features · private admin workbench</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-zinc-200">
              {committee.map((f, i) => (
                <div key={i} className="p-5 border-r border-b border-zinc-200 flex gap-3 items-start bg-white">
                  <CheckCircle size={20} weight="fill" className="text-zinc-900 flex-shrink-0 mt-0.5" />
                  <p className="text-zinc-800 leading-relaxed text-sm md:text-base">{f}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OPTION B — Two columns side by side */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="app-container py-14 md:py-16">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-10 w-10 bg-zinc-950 text-white flex items-center justify-center font-black text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>B</div>
            <div>
              <p className="overline text-zinc-500">Option B</p>
              <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Side by side — Residents column + Committee column</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-zinc-200 bg-white">
            {/* Residents column */}
            <div className="p-6 md:p-8 border-r border-b lg:border-b-0 border-zinc-200">
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5 pb-3 border-b border-zinc-200">
                <div className="flex items-center gap-2">
                  <Users size={20} weight="duotone" className="text-[hsl(var(--appistry-signal))]" />
                  <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>For residents</h3>
                </div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{residents.length} · public</p>
              </div>
              <ul className="space-y-3.5">
                {residents.map((f, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <CheckCircle size={18} weight="fill" className="text-[hsl(var(--appistry-signal))] flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-800 leading-snug text-sm">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* Committee column */}
            <div className="p-6 md:p-8 bg-zinc-950 text-white">
              <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5 pb-3 border-b border-zinc-800">
                <div className="flex items-center gap-2">
                  <ShieldCheck size={20} weight="duotone" className="text-[hsl(var(--appistry-signal))]" />
                  <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{adminLabel}</h3>
                </div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{committee.length} · private</p>
              </div>
              <ul className="space-y-3.5">
                {committee.map((f, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <CheckCircle size={18} weight="fill" className="text-[hsl(var(--appistry-signal))] flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-200 leading-snug text-sm">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-xs font-mono text-zinc-500 mt-4 italic">
            Note: the asymmetric count (7 vs 15) means the Committee column will be taller than the Residents column on desktop. That's a feature of this layout — it visually reinforces that "the committee side is the bigger half of the product".
          </p>
        </div>
      </section>

      {/* Footer note */}
      <div className="app-container py-12 text-center">
        <p className="overline text-zinc-500 mb-2">Once you pick</p>
        <p className="text-lg text-zinc-700">
          Tell me "<span className="font-mono bg-zinc-100 px-2 py-0.5">use A</span>" or "<span className="font-mono bg-zinc-100 px-2 py-0.5">use B</span>" and I'll apply it to the live Holborough case study.
        </p>
      </div>
    </div>
  );
}
